"use client";

/**
 * UI d'erreur de marque, localisée (FR / EN / AR — MSA), partagée par tous les
 * `error.tsx` (root, (global), (landing-page)). Détecte la locale via le
 * pathname, propose un bouton « réessayer » (rejoue le segment via `reset`) et
 * un lien « retour à l'accueil » pointant vers l'accueil de la bonne locale.
 *
 * Composant CLIENT (requis : `error.tsx` est toujours un Client Component, et on
 * lit `usePathname`). RTL-safe : classes logiques (`text-start`, `ms`/`me`) →
 * fonctionne tel quel sous `/ar` (dir=rtl posé sur <html> par le layout).
 *
 * Volontairement SANS dépendance au registre d'icônes (icônes inline en SVG) ni
 * aux effets WebGL : une page d'erreur doit rester ultra-robuste et légère.
 */

import Link from "next/link";
import { usePathname } from "next/navigation";
import { localeFromPath } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { tr, type I18n } from "@/config/localeText";

export type ErrorViewProps = {
  /** Fourni par le boundary `error.tsx` Next : rejoue le rendu du segment. */
  reset?: () => void;
};

type ErrorCopy = {
  eyebrow: string;
  title: string;
  body: string;
  retry: string;
  home: string;
};

const ERROR_UI: I18n<ErrorCopy> = {
  fr: {
    eyebrow: "Erreur",
    title: "Une erreur est survenue",
    body: "Un incident technique a interrompu le chargement de cette page. Vous pouvez réessayer ou revenir à l'accueil.",
    retry: "Réessayer",
    home: "Retour à l'accueil",
  },
  en: {
    eyebrow: "Error",
    title: "Something went wrong",
    body: "A technical issue interrupted the loading of this page. You can try again or go back to the homepage.",
    retry: "Try again",
    home: "Back to home",
  },
  ar: {
    eyebrow: "خطأ",
    title: "حدث خطأ ما",
    body: "حدث خلل تقني أثناء تحميل هذه الصفحة. يمكنك إعادة المحاولة أو العودة إلى الصفحة الرئيسية.",
    retry: "إعادة المحاولة",
    home: "العودة إلى الصفحة الرئيسية",
  },
};

export function ErrorView({ reset }: ErrorViewProps): JSX.Element {
  const pathname = usePathname();
  const locale = localeFromPath(pathname ?? "/");
  const t = tr(ERROR_UI, locale);
  const homeHref = localeNavHref("/", locale);

  return (
    <main className="flex min-h-screen items-center justify-center bg-black px-4 py-16 text-white sm:px-6 md:py-24">
      <section className="w-full max-w-xl text-start">
        <p className="mb-5 inline-block rounded-full border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-white/70 sm:text-xs sm:tracking-[0.35em]">
          {t.eyebrow}
        </p>

        <h1
          className="mb-4 bg-clip-text text-3xl font-semibold leading-tight text-transparent sm:text-4xl"
          style={{
            backgroundImage: "linear-gradient(135deg, #D4AF37, #f5d57a)",
            WebkitBackgroundClip: "text",
          }}
        >
          {t.title}
        </h1>

        <p className="mb-8 max-w-prose text-sm text-white/75 sm:text-base">
          {t.body}
        </p>

        <div className="flex flex-wrap gap-3">
          {reset ? (
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white md:px-6"
            >
              {t.retry}
            </button>
          ) : null}
          <Link
            href={homeHref}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/90 transition-colors hover:border-primary hover:text-primary md:px-6"
          >
            {t.home}
          </Link>
        </div>
      </section>
    </main>
  );
}

export default ErrorView;
